import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import Logo from "../components/Logo";

export default function SubscribePage() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [regions, setRegions] = useState(["all"]);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const REGION_OPTIONS = [
    { value: "europe",      label: "Flying from Europe" },
    { value: "asia",        label: "Flying from Asia" },
    { value: "americas",    label: "Flying from the Americas" },
    { value: "africa",      label: "Flying from Africa" },
    { value: "middle_east", label: "Flying from the Middle East" },
    { value: "oceania",     label: "Flying from Oceania" },
  ];

  const toggleRegion = (value) => {
    if (value === "all") {
      setRegions(["all"]);
    } else {
      setRegions((prev) => {
        const filtered = prev.filter((r) => r !== "all" && r !== value);
        const next = prev.includes(value) ? filtered : [...filtered, value];
        return next.length === 0 ? ["all"] : next;
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", {
        email,
        first_name: firstName || null,
        consent_given_at: new Date().toISOString(),
        regions,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-center px-4 py-16">
      <Helmet>
        <link rel="canonical" href="https://restricteddeals.com/subscribe" />
      </Helmet>
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="inline-block mb-4">
          <Logo />
        </Link>

        <Link
          to="/"
          style={{
            color: "#CC1A1A",
            fontFamily: "monospace",
            fontSize: "13px",
            letterSpacing: "0.1em",
            display: "block",
            marginBottom: "24px",
          }}
        >
          ← BACK TO DEALS
        </Link>

        {done ? (
          /* Success state */
          <div data-testid="subscribe-success">
            <p className="eyebrow eyebrow-red">// ACCESS REQUESTED</p>
            <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight mt-4 leading-tight">
              Check your email<br />to confirm.
            </h1>
            <p className="mt-6 text-[#9A8F83] font-sans leading-relaxed">
              We sent a confirmation link to <strong className="text-white">{email}</strong>.
              Click it to activate your free deal alerts.
            </p>
            <Link to="/" className="inline-block mt-10 text-[11px] font-mono tracking-[0.18em] uppercase text-[#888] hover:text-[#CC1A1A] transition-colors">
              ← Back to all deals
            </Link>
          </div>
        ) : (
          /* Subscribe form */
          <form onSubmit={submit} data-testid="subscribe-form">
            <p className="eyebrow eyebrow-red">// FREE DEAL ALERTS</p>

            <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight mt-4 leading-tight">
              Get free<br />deal alerts
            </h1>

            <p className="font-mono text-sm text-[#CC1A1A] tracking-[0.12em] uppercase mt-4">
              deals not on public record
            </p>

            <p className="mt-4 text-[#888888] font-sans leading-relaxed text-sm">
              Flights, hotels, cruises and packages — hand-picked and delivered
              to your inbox before they sell out. Free. No spam.
            </p>

            <div className="mt-8 space-y-3">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="w-full bg-[#0E0E0E] border border-[#2A2A2A] text-white placeholder-[#888] px-4 py-4 outline-none focus:border-[#CC1A1A] transition-colors font-mono text-sm"
                autoComplete="given-name"
                data-testid="subscribe-firstname"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="operator@inbox.com"
                className="w-full bg-[#0E0E0E] border border-[#2A2A2A] text-white placeholder-[#888] px-4 py-4 outline-none focus:border-[#CC1A1A] transition-colors font-mono text-sm"
                autoComplete="email"
                data-testid="subscribe-email"
              />

              {/* Region preferences */}
              <div className="border border-[#2A2A2A] bg-[#0E0E0E] px-4 py-4">
                <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#555] mb-1">
                  Where are you flying FROM?
                </p>
                <p className="text-[10px] font-sans text-[#555] mb-3 leading-relaxed">
                  Select your departure region — we'll send you deals departing from your chosen region to anywhere in the world.
                </p>
                <div className="space-y-2">
                  {REGION_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={regions.includes(opt.value)}
                        onChange={() => toggleRegion(opt.value)}
                        className="w-4 h-4 shrink-0 accent-[#CC1A1A]"
                      />
                      <span className="text-[11px] font-mono tracking-[0.1em] uppercase text-[#CCCCCC]">{opt.label}</span>
                    </label>
                  ))}
                  <label className="flex items-center gap-3 cursor-pointer border-t border-[#2A2A2A] pt-2 mt-1">
                    <input
                      type="checkbox"
                      checked={regions.includes("all")}
                      onChange={() => toggleRegion("all")}
                      className="w-4 h-4 shrink-0 accent-[#CC1A1A]"
                    />
                    <span className="text-[11px] font-mono tracking-[0.1em] uppercase text-[#888]">Send me all deals <span className="text-[#555]">(recommended)</span></span>
                  </label>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="subscribe-consent-label">
                <input
                  type="checkbox"
                  required
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 shrink-0 accent-[#CC1A1A]"
                  data-testid="subscribe-consent"
                />
                <span className="text-[11px] font-mono tracking-[0.12em] uppercase text-[#CCCCCC] leading-relaxed">
                  I agree to the{" "}
                  <Link to="/privacy" className="text-[#CC1A1A] hover:underline">
                    Privacy Policy
                  </Link>{" "}
                  and consent to receive deal alerts by email. I confirm I am 16 or older.
                </span>
              </label>
              <button
                type="submit"
                disabled={loading || !consent}
                className="w-full bg-[#CC1A1A] hover:bg-[#AA1515] text-white font-mono text-xs tracking-[0.2em] uppercase py-4 transition-colors disabled:opacity-60"
                data-testid="subscribe-submit"
              >
                {loading ? "TRANSMITTING…" : "GET FREE DEAL ALERTS →"}
              </button>
            </div>

            <p className="mt-4 text-[11px] font-mono tracking-[0.14em] uppercase text-[#AAAAAA] text-center">
              Free. No spam. Unsubscribe anytime.
            </p>

            <div className="mt-10 border-t border-[#1E1E1E] pt-6">
              <Link to="/" className="text-[11px] font-mono tracking-[0.18em] uppercase text-[#888] hover:text-[#CC1A1A] transition-colors">
                ← Back to all deals
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
